import type { BatchRecord, SalesRecord, WarehouseDataset } from "../types";
import { resolveBatchExpiry } from "./batch";

type CsvRow = Record<string, string>;

const aliases = {
  date: ["date", "日期", "出库日期", "订单日期", "创建时间", "created_at", "order_date"],
  sku: ["sku", "货号", "商品编码", "物料编码", "item_id"],
  product: ["product", "商品", "商品名称", "产品名", "品名", "规格名称", "name"],
  quantity: ["quantity", "qty", "数量", "出库数量", "件数"],
  orders: ["orders", "订单数", "频次", "frequency"],
  location: ["location", "库位", "货位", "区位", "location_code"],
  lot: ["lot", "批次", "批号", "生产批次", "batch", "batch_no"],
  expiresOn: ["expiresOn", "expiry", "expires_on", "到期日", "有效期", "保质期至"],
  producedOn: ["producedOn", "productionDate", "produced_on", "生产日期", "生产日", "制造日期"],
  shelfLifeDays: ["shelfLifeDays", "shelf_life_days", "保质期天数", "保质天数"],
  customer: ["customer", "客户", "客户名称"],
  currentStock: ["currentStock", "current_stock", "库存", "现有库存", "库存数量", "stock"],
  incomingStock: ["incomingStock", "incoming_stock", "在途库存", "在途", "在途数量", "purchaseInTransit"],
  recallFlag: ["recallFlag", "召回", "召回标记"],
} as const;

function normalizeHeader(header: string) {
  return header.trim().replace(/^\uFEFF/, "");
}

function parseCsv(text: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"' && quoted && next === '"') {
      cell += '"';
      i++;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") i++;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  if (rows.length < 2) throw new Error("CSV 至少需要表头和一行数据。");
  const headers = rows[0].map(normalizeHeader);
  return rows.slice(1).map((values) =>
    headers.reduce<CsvRow>((record, header, index) => {
      record[header] = values[index]?.trim() ?? "";
      return record;
    }, {}),
  );
}

function col(row: CsvRow, keys: readonly string[]) {
  for (const key of keys) {
    if (row[key] != null && row[key] !== "") return row[key];
  }
  return "";
}

function parseNumber(value: string, fallback = 0) {
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function optionalPositiveNumber(value: unknown) {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function parseBoolean(value: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase();
  return ["true", "1", "yes", "y", "是", "召回"].includes(normalized);
}

function normalizeDate(value: string) {
  if (!value) return "";
  const cleaned = value.trim().replace(/[/.]/g, "-");
  const calendar = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (calendar) {
    const year = Number(calendar[1]);
    const month = Number(calendar[2]);
    const day = Number(calendar[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
    ) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return "";
  }
  const date = new Date(cleaned);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function addDays(value: string, days: number) {
  const date = new Date(`${value}T12:00:00+08:00`);
  if (Number.isNaN(date.getTime()) || !Number.isFinite(days)) return "";
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function normalizeBatch(record: Partial<BatchRecord>, index: number): BatchRecord | null {
  const sku = String(record.sku ?? "").trim();
  const lot = String(record.lot ?? "").trim();
  if (!sku || !lot) return null;

  const rawExpiry = String(record.expiresOn ?? "");
  const producedOn = normalizeDate(String(record.producedOn ?? ""));
  const shelfLifeDays = optionalPositiveNumber(record.shelfLifeDays);
  const expiresOn =
    resolveBatchExpiry({ expiresOn: rawExpiry, lot })
    || (producedOn && shelfLifeDays ? addDays(producedOn, shelfLifeDays) : "");

  return {
    id: String(record.id ?? `B-${sku}-${lot}-${index + 1}`),
    sku,
    product: String(record.product ?? sku).trim() || sku,
    lot,
    expiresOn,
    producedOn: producedOn || undefined,
    shelfLifeDays,
    quantity: Math.max(0, parseNumber(String(record.quantity ?? "0"))),
    location: String(record.location ?? "UNASSIGNED").trim() || "UNASSIGNED",
    recallFlag: parseBoolean(record.recallFlag),
  };
}

function mergeBatches(records: Partial<BatchRecord>[]) {
  const merged = new Map<string, BatchRecord>();
  const usedIds = new Set<string>();

  records.forEach((record, index) => {
    const batch = normalizeBatch(record, index);
    if (!batch) return;
    const key = `${batch.sku}::${batch.lot}`;
    const existing = merged.get(key);

    if (existing) {
      const expiresOn = existing.expiresOn && batch.expiresOn
        ? (existing.expiresOn < batch.expiresOn ? existing.expiresOn : batch.expiresOn)
        : existing.expiresOn || batch.expiresOn;
      merged.set(key, {
        ...existing,
        expiresOn,
        producedOn: existing.producedOn || batch.producedOn,
        shelfLifeDays: existing.shelfLifeDays ?? batch.shelfLifeDays,
        quantity: existing.quantity + batch.quantity,
        location: existing.location === "UNASSIGNED" ? batch.location : existing.location,
        recallFlag: existing.recallFlag || batch.recallFlag,
      });
      return;
    }

    let id = batch.id;
    let suffix = 2;
    while (usedIds.has(id)) id = `${batch.id}-${suffix++}`;
    usedIds.add(id);
    merged.set(key, { ...batch, id });
  });

  return [...merged.values()];
}

function fromRows(rows: CsvRow[], fileName: string): WarehouseDataset {
  const sales: SalesRecord[] = [];
  const batches: Partial<BatchRecord>[] = [];
  rows.forEach((row, index) => {
    const date = normalizeDate(col(row, aliases.date));
    const sku = col(row, aliases.sku) || `IMPORT-${index + 1}`;
    const quantity = Math.abs(parseNumber(col(row, aliases.quantity)));
    if (!date) return;
    const product = col(row, aliases.product) || sku;
    const location = col(row, aliases.location) || "UNASSIGNED";
    const lot = col(row, aliases.lot) || col(row, ["生产批次"]);
    const producedOn = normalizeDate(col(row, aliases.producedOn));
    const shelfLifeDays = optionalPositiveNumber(col(row, aliases.shelfLifeDays));
    const expiresOn =
      resolveBatchExpiry({ lot, expiresOn: col(row, aliases.expiresOn) })
      || (producedOn && shelfLifeDays ? addDays(producedOn, shelfLifeDays) : "");
    const recallFlag = parseBoolean(col(row, aliases.recallFlag));
    sales.push({
      id: `R-${index}`,
      date,
      sku,
      product,
      quantity,
      orders: parseNumber(col(row, aliases.orders), 1),
      location,
      lot: lot || undefined,
      expiresOn: expiresOn || undefined,
      producedOn: producedOn || undefined,
      shelfLifeDays,
      recallFlag,
      customer: col(row, aliases.customer) || undefined,
      currentStock: col(row, aliases.currentStock) ? parseNumber(col(row, aliases.currentStock)) : undefined,
      incomingStock: col(row, aliases.incomingStock) ? parseNumber(col(row, aliases.incomingStock)) : undefined,
    });
    if (lot && expiresOn) {
      batches.push({
        id: `B-${sku}-${lot}`,
        sku,
        product,
        lot,
        expiresOn,
        producedOn: producedOn || undefined,
        shelfLifeDays,
        quantity,
        location,
        recallFlag,
      });
    }
  });
  if (!sales.length) throw new Error("未识别到有效日期列，请检查“日期 / date”字段。");
  const dates = [...new Set(sales.map((sale) => sale.date))].sort();
  return {
    meta: {
      name: fileName,
      importedAt: new Date().toISOString(),
      source: "file",
      asOf: dates.at(-1) ?? new Date().toISOString().slice(0, 10),
      warehouse: "当前仓库",
    },
    sales,
    batches: mergeBatches(batches),
  };
}

export async function parseDatasetFile(file: File): Promise<WarehouseDataset> {
  const text = await file.text();
  if (file.name.toLowerCase().endsWith(".json")) {
    const parsed = JSON.parse(text) as Partial<WarehouseDataset>;
    if (!Array.isArray(parsed.sales) || !parsed.sales.length) {
      throw new Error("JSON 需要包含 sales 数组。");
    }
    const sales = parsed.sales.map((sale, index) => ({
      ...sale,
      id: String(sale.id ?? `R-${index}`),
      date: normalizeDate(String(sale.date ?? "")),
      sku: String(sale.sku ?? `IMPORT-${index + 1}`),
      product: String(sale.product ?? sale.sku ?? `IMPORT-${index + 1}`),
      quantity: Math.max(0, Number(sale.quantity) || 0),
      location: sale.location ? String(sale.location) : undefined,
      lot: sale.lot ? String(sale.lot) : undefined,
      expiresOn: normalizeDate(String(sale.expiresOn ?? "")) || undefined,
      producedOn: normalizeDate(String(sale.producedOn ?? "")) || undefined,
      shelfLifeDays: optionalPositiveNumber(sale.shelfLifeDays),
      recallFlag: parseBoolean(sale.recallFlag),
      currentStock: Number.isFinite(Number(sale.currentStock)) ? Number(sale.currentStock) : undefined,
      incomingStock: Number.isFinite(Number(sale.incomingStock)) ? Number(sale.incomingStock) : undefined,
    }));
    if (!sales.length) throw new Error("JSON 中没有可识别的有效销售记录。");
    return {
      meta: {
        name: file.name,
        importedAt: new Date().toISOString(),
        source: "file",
        asOf: normalizeDate(String(parsed.meta?.asOf ?? "")) || sales.map((sale) => sale.date).sort().at(-1) || "",
        warehouse: parsed.meta?.warehouse ?? "当前仓库",
      },
      sales,
      batches: mergeBatches(parsed.batches ?? []),
    };
  }
  return fromRows(parseCsv(text), file.name);
}

export function datasetToCsv(dataset: WarehouseDataset) {
  const columns = [
    ["date", "date"],
    ["sku", "sku"],
    ["product", "product"],
    ["quantity", "quantity"],
    ["orders", "orders"],
    ["location", "location"],
    ["lot", "lot"],
    ["expiresOn", "expiresOn"],
    ["生产日期", "producedOn"],
    ["保质期天数", "shelfLifeDays"],
    ["召回标记", "recallFlag"],
    ["customer", "customer"],
    ["currentStock", "currentStock"],
    ["incomingStock", "incomingStock"],
  ] as const satisfies ReadonlyArray<readonly [string, keyof SalesRecord]>;
  const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  return [
    columns.map(([header]) => header).join(","),
    ...dataset.sales.map((row) => columns.map(([, field]) => escape(row[field])).join(",")),
  ].join("\n");
}
